import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { ApiEndpoints } from '../../../shared/common/config';
import { environment } from '../../../../environments/environment';

export interface DashboardChartsRequest {
  locationId: number;
}

export interface DashboardStatisticRequest {
  locationId: number;
}

export interface DashboardState {
  activeTabIndex: number;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly baseUrl = environment.mainapiUrl;
  
  // Dashboard state management
  private readonly dashboardState: DashboardState = {
    activeTabIndex: 0, // Default to first tab (Overview)
  };

  constructor(private readonly http: HttpClient) {}

  setActiveTab(index: number): void {
    this.dashboardState.activeTabIndex = index;
  }

  getActiveTab(): number {
    return this.dashboardState.activeTabIndex;
  }

  clearState(): void {
    this.dashboardState.activeTabIndex = 0;
  }

  /**
   * Handle HTTP errors with fallback data
   */
  private handleError(operation = 'operation', result?: any) {
    return (error: any): Observable<any> => {
      console.error(`${operation} failed:`, error);
      // Return fallback result to keep app running
      return of(result);
    };
  }

  /**
   * get grid Data leads
   */
  getClientDashboard(data: any): Observable<any> {
    const url = ApiEndpoints.dashboards.GetDashboard;
    return this.http.post<any>(url, data).pipe(
      retry(1),
      catchError(
        this.handleError('getClientDashboard', {
          data: [],
          message: 'Mock data',
        }),
      ),
    );
  }

  /**
   * Get dashboard charts data
   */
  public getDashboardCharts(locationId: number): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/Charts/GetDashboardCharts/${locationId}`, {
        locationId: locationId,
      })
      .pipe(
        retry(1),
        catchError(
          this.handleError('getDashboardCharts', {
            charts: [],
            message: 'Using mock data due to API unavailability',
          }),
        ),
      );
  }

  /**
   * Get dashboard statistics
   */
  public getDashboardStatistic(locationId: number): Observable<any> {
    return this.http
      .post(`${this.baseUrl}/Charts/GetDashboardStatistic/${locationId}`, {
        locationId: locationId,
      })
      .pipe(
        retry(1),
        catchError(
          this.handleError('getDashboardStatistic', {
            statistics: {
              totalRides: 0,
              totalDrivers: 0,
              totalMiles: 0,
              totalEvents: 0,
            },
            message: 'Using mock data due to API unavailability',
          }),
        ),
      );
  }

  /**
   * Get dashboard overview data (combination of charts and statistics)
   */
  public getDashboardOverview(locationId: number): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/dashboard/overview/${locationId}`)
      .pipe(
        retry(1),
        catchError(
          this.handleError('getDashboardOverview', {
            overview: {
              totalRides: 150,
              totalDrivers: 25,
              totalMiles: 4567.8,
              totalEvents: 89,
              recentActivity: [],
            },
            message: 'Using mock data due to API unavailability',
          }),
        ),
      );
  }
}
